import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SUPPLIER_TRADE_LABELS, formatRef, parseRef, requiresRegistration } from '@promanage/core';
import { Button } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import {
  DEFAULT_SUPPLIER_SORT,
  SUPPLIER_COLUMNS,
  type SupplierColumnId,
} from '../features/suppliers/supplierColumns';
import type { SupplierRow } from '../features/suppliers/types';
import { trpc } from '../lib/trpc';
import { MaintenanceNav } from '../features/maintenance/MaintenanceNav';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';

export const Route = createFileRoute('/maintenance/contractors/')({
  component: ContractorsPage,
});

const STORAGE_KEY = 'promanage:supplier-columns';

/** One identity, so the pending state does not re-sort the table on every render. */
const NO_SUPPLIERS: SupplierRow[] = [];

type SupplierFilter = 'active' | 'unpayable' | 'unregistered' | 'all';

const FILTERS: { value: SupplierFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'unpayable', label: 'No bank account' },
  { value: 'unregistered', label: 'Registration missing' },
  { value: 'all', label: 'All' },
];

/**
 * Contractors — the trades and services the agency spends an owner's money with.
 *
 * `/maintenance/contractors` RATHER THAN `/suppliers`, because that is what a property manager
 * calls them. The table, the schema and the API all say supplier, which is the accounting word and the one a
 * bill will be from; the URL and the tab say the word somebody would type.
 *
 * ARCHIVED ARE HIDDEN BY DEFAULT and the filter starts on Active — unlike the owners list, which
 * starts on All. An agency accumulates contractors it stopped using and keeps them for the history
 * on old jobs; an agency does not accumulate owners it stopped managing in anything like the same
 * numbers, and one archived owner is worth noticing.
 *
 * TWO OF THE FOUR FILTERS ARE BACKLOGS. "No bank account" is the invoice that cannot be paid
 * today; "Registration missing" is the gasfitter nobody has the PGDB number for, which is the one
 * that turns into a declined insurance claim.
 */
function ContractorsPage() {
  const navigate = useNavigate();
  const list = useQuery(
    trpc.suppliers.list.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load the contractors' },
      staleTime: 10_000,
    }),
  );
  const suppliers = list.data ?? NO_SUPPLIERS;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<SupplierFilter>('active');
  const [sort, setSort] = useState<SortState<SupplierColumnId>>(DEFAULT_SUPPLIER_SORT);
  const columns = useColumnState(STORAGE_KEY, SUPPLIER_COLUMNS);
  const workspace = useWorkspace({
    route: '/maintenance/contractors',
    state: { filters: { query, filter }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, SUPPLIER_COLUMNS, DEFAULT_SUPPLIER_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'active',
        ),
      );
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return suppliers
      .filter((supplier) => {
        if (filter === 'all') return true;
        // The two backlogs are about contractors the agency still uses. An archived plumber with
        // no bank account is not a job anybody has to do.
        if (supplier.status !== 'active') return false;
        if (filter === 'unpayable') return !supplier.hasBankAccount;
        if (filter === 'unregistered') {
          return requiresRegistration(supplier.trade) && supplier.registrationNumber === null;
        }
        return true;
      })
      .filter((supplier) => {
        if (!q) return true;

        const asRef = parseRef(q);
        if (asRef) return asRef.entity === 'supplier' && asRef.sequence === supplier.refSeq;

        return [
          supplier.name,
          formatRef('supplier', supplier.refSeq),
          SUPPLIER_TRADE_LABELS[supplier.trade],
          supplier.registrationNumber,
          supplier.gstNumber,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, SUPPLIER_COLUMNS));
  }, [suppliers, query, filter, sort]);

  const total = suppliers.length;
  const summary = list.isPending
    ? 'Loading…'
    : rows.length === total
      ? `${total} ${total === 1 ? 'supplier' : 'suppliers'}`
      : `Showing ${rows.length} of ${total} contractors`;

  return (
    <ListShell
      nav={<MaintenanceNav />}
      title="Contractors"
      subtitle={summary}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={SUPPLIER_COLUMNS}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          <Link to="/maintenance/contractors/new">
            <Button size="sm">
              <span aria-hidden="true">+</span> Add contractor
            </Button>
          </Link>
        </>
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search name, ref (SU000001), trade or registration"
      searchLabel="Search contractors"
      filters={
        <FilterChips
          label="Filter contractors"
          value={filter}
          options={FILTERS}
          onChange={setFilter}
        />
      }
      isPending={list.isPending}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          title={query ? `Nothing matches “${query}”` : 'No contractors yet'}
          action={
            <Link to="/maintenance/contractors/new">
              <Button size="sm">Add contractor</Button>
            </Link>
          }
        >
          {query
            ? 'Try a different name, ref, trade or registration number.'
            : 'A contractor is the company that holds the bank account and the registration — the tradesperson who turns up is a person attached to it. Where they are paid is recorded separately, by a trust administrator.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(supplier) => supplier.id}
        onOpen={(supplier) =>
          void navigate({
            to: '/maintenance/contractors/$ref',
            params: { ref: formatRef('supplier', supplier.refSeq) },
          })
        }
        caption={`Contractors, one row per contractor. ${summary}.`}
        minWidth="52rem"
      />
    </ListShell>
  );
}
